import {useEffect, useRef, useState} from 'react'
import {buildWalkIllustration, illustrationDrawMs} from '@/features/navigation/staticWalkIllustration'

/**
 * THE WALK, DRAWN — a ghost of the whole route with the part already walked filled in.
 *
 * ── ⚠️ THE GHOST IS THE POINT, NOT THE FILLED LINE ────────────────────────────────
 * Showing only the completed part would answer "where have I been", which nobody is
 * asking. The dashed full path is there so a passenger on step 1 of 3 can see that two
 * more corners are coming and roughly what shape they are — that is what stops the screen
 * feeling like a blindfolded sequence of instructions.
 *
 * ── ⚠️ IT IS NOT A MAP AND MUST NOT BE READ AS ONE ────────────────────────────────
 * No north, no scale, no relationship to the building. See `staticWalkIllustration` for
 * why the proportions are deliberately compressed. It is a diagram of the turns.
 */

/* The house mint at two weights: the walked line, and the disc a passenger is drawn on. */
const DRAWN = '#8FFAD7'
const GHOST = '#CFCDCD'
const PIN_DARK = '#201E20'
const PIN_MINT = '#A9FFE3'

/** A path string through the points, up to `count` of them. */
const lineThrough = (points) => points.map((p, i) => `${i ? 'L' : 'M'}${p.x} ${p.y}`).join(' ')

/**
 * @param {Array} steps the recorded walk
 * @param {number} index which step the passenger is on; `steps.length` means arrived
 */
export default function WalkIllustration({steps, index}) {
    const {points, width, height} = buildWalkIllustration(steps)

    const arrived = index >= steps.length
    /* The vertex the walk has reached. On the arrival card that is the end of the path. */
    const reached = Math.min(index, Math.max(0, points.length - 1))
    const activeMetres = arrived ? 0 : Number(steps[index]?.distanceMeters) || 0
    /* Nothing to grow into on the last vertex or the arrival card: the line is complete. */
    const growing = !arrived && reached + 1 < points.length

    /*
     * HOW FAR ALONG THE ACTIVE SEGMENT THE LINE HAS GROWN, 0→1.
     *
     * ⚠️ ANIMATED IN A FRAME LOOP RATHER THAN BY CSS, because the line is a polyline
     * whose vertices are data: there is no single property to transition.
     * `stroke-dashoffset` would animate the whole path as one length and slide the fill
     * through the corners at the wrong moments, since the segments are not equal.
     *
     * ⚠️ THE SEGMENT IS STORED WITH THE FRACTION, and that pairing is what stops a
     * flash. Pressing Next re-renders immediately, but the first animation frame is up to
     * 16 ms later — so a bare fraction left over from the previous segment would draw the
     * passenger at the FAR END of the new one for that frame, a visible jump forward and
     * back. Tagged with the segment it belongs to, a stale fraction simply reads as zero.
     */
    const [drawn, setDrawn] = useState({segment: -1, t: 0})
    const frameRef = useRef(0)

    useEffect(() => {
        cancelAnimationFrame(frameRef.current)
        if (!growing) return undefined

        const duration = illustrationDrawMs(activeMetres)
        const startedAt = performance.now()

        const tick = (now) => {
            const t = Math.min(1, (now - startedAt) / duration)
            /* easeInOutCubic — a line that starts and stops abruptly reads as a glitch
               rather than as movement. */
            const eased = t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2
            setDrawn({segment: reached, t: eased})
            if (t < 1) frameRef.current = requestAnimationFrame(tick)
        }
        frameRef.current = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(frameRef.current)
    }, [reached, growing, activeMetres])

    const progress = growing ? (drawn.segment === reached ? drawn.t : 0) : 1

    /* Where the passenger's marker sits: at the reached vertex, or partway to the next. */
    const from = points[reached] || points[0]
    const to = points[reached + 1] || from
    const head = {
        x: from.x + (to.x - from.x) * progress,
        y: from.y + (to.y - from.y) * progress,
    }

    const walked = [...points.slice(0, reached + 1), head]
    const end = points[points.length - 1]

    return (
        /* ⚠️ `preserveAspectRatio` KEEPS THE ANGLES HONEST. Letting the viewBox stretch to
           the stage would turn a recorded 90° corner into whatever the container's aspect
           ratio made of it, and the corner is the instruction. */
        <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            className="h-full w-full"
            role="img"
            aria-label={arrived ? 'Walk complete' : `Step ${index + 1} of ${steps.length}`}
        >
            {/* The whole route, faint. */}
            <path
                d={lineThrough(points)}
                fill="none"
                stroke={GHOST}
                strokeWidth="3"
                strokeDasharray="2 7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {/* The part already walked. */}
            <path
                d={lineThrough(walked)}
                fill="none"
                stroke={DRAWN}
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <circle cx={points[0].x} cy={points[0].y} r="5" fill={DRAWN}/>

            {/* THE PICKUP, at the end — a dark disc with a mint core, the same pin the live
                map uses for the booth so the two screens agree about what a destination
                looks like. */}
            <circle cx={end.x} cy={end.y} r="14" fill={PIN_DARK}/>
            <circle cx={end.x} cy={end.y} r="6" fill={PIN_MINT}/>

            {/* THE PASSENGER, riding the tip of the growing line. */}
            <circle cx={head.x} cy={head.y} r="14" fill={PIN_MINT} stroke={PIN_DARK} strokeWidth="2"/>
            <g transform={`translate(${head.x - 9} ${head.y - 9})`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PIN_DARK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="3.2"/>
                    <path d="M5.5 20a6.5 6.5 0 0 1 13 0"/>
                </svg>
            </g>
        </svg>
    )
}

export {WalkIllustration}
